import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, X } from 'lucide-react';
import { useBrandProductMoreTags } from '@/hooks/useBrandProductMoreTags';
import { useBrandTerminology } from '@/hooks/useBrandTerminology';

interface ProductMoreTagSelectorProps {
  brandId: string;
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  className?: string;
}

export const ProductMoreTagSelector = ({
  brandId,
  selectedTagIds,
  onTagsChange,
  className = ''
}: ProductMoreTagSelectorProps) => {
  const [newTagName, setNewTagName] = useState('');
  const { getTerminology } = useBrandTerminology();
  const { tags, createTag, isCreating } = useBrandProductMoreTags(brandId);

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));
  const availableTags = tags.filter(tag => !selectedTagIds.includes(tag.id));

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagsChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTagIds, tagId]);
    }
  };

  const removeTag = (tagId: string) => {
    onTagsChange(selectedTagIds.filter(id => id !== tagId));
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;

    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    createTag({
      brand_id: brandId,
      name: newTagName.trim(),
      color: randomColor,
    });

    setNewTagName('');
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="space-y-2">
        <label className="text-sm font-medium">{getTerminology('more_tags', { plural: true })}</label>
        
        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map(tag => (
              <Badge 
                key={tag.id}
                style={{ backgroundColor: tag.color }}
                className="text-white hover:opacity-80 cursor-pointer"
              >
                {tag.name}
                <X 
                  className="ml-1 h-3 w-3 cursor-pointer" 
                  onClick={() => removeTag(tag.id)}
                />
              </Badge>
            ))}
          </div>
        )}

        {/* Available Tags */}
        {availableTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <Badge 
                key={tag.id}
                variant="outline"
                style={{ borderColor: tag.color, color: tag.color }}
                className="cursor-pointer hover:opacity-70"
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        )}

        {/* Add New Tag */}
        <div className="flex gap-2">
          <Input
            placeholder="Add new more tag..."
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateTag();
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
